import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "component/common/breadcrumb";
import strings from "translate/language";
import {
  ChangePassword as changePasswordAction,
  forgotRemoveData,
  clearMustChangePassword,
  userLogout,
} from "reduxStore/action/authActions";
import authService from "reduxStore/service/authService";
import { loginMustChangePassword, seo } from "utils/utils";
import "./ChangePassword.css";

const EMPTY_FORM = {
  old_password: "",
  new_password: "",
  confirm_password: "",
};

const ChangePassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formInput, setFormInput] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(EMPTY_FORM);
  const [currentPasswordStatus, setCurrentPasswordStatus] = useState("");
  const [newPasswordStatus, setNewPasswordStatus] = useState("");
  const currentPasswordTimer = useRef(null);
  const currentPasswordRequest = useRef(0);
  const newPasswordTimer = useRef(null);
  const newPasswordRequest = useRef(0);

  const changePasswordSelector =
    useSelector((state) => state.changePassword) || {};
  const loginSelector = useSelector((state) => state.login);
  const mustChangePassword = loginMustChangePassword(loginSelector?.response);

  useEffect(() => {
    return () => {
      if (currentPasswordTimer.current) {
        clearTimeout(currentPasswordTimer.current);
      }
      if (newPasswordTimer.current) {
        clearTimeout(newPasswordTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    seo({
      title: strings.changePassword,
      metaDescription: strings.enterNewPass,
      canonicalLink: window.location.href,
    });
  }, []);

  useEffect(() => {
    const { response, change = false } = changePasswordSelector || {};
    if (response && change && response?.status === 200) {
      dispatch(forgotRemoveData());
      dispatch(clearMustChangePassword());
      navigate("/dashboard");
    }
  }, [changePasswordSelector, dispatch, navigate]);

  const verifyNewPasswordValue = async (value, oldPassword) => {
    const password = String(value || "");
    if (!password.trim()) {
      setNewPasswordStatus("");
      return;
    }
    const requestId = newPasswordRequest.current + 1;
    newPasswordRequest.current = requestId;
    setNewPasswordStatus("checking");
    try {
      const res = await authService.verifyNewPassword({
        new_password: password,
        old_password: oldPassword,
        suppressGlobalLoader: true,
      });
      if (requestId !== newPasswordRequest.current) {
        return;
      }
      if (res?.data?.status === 200 && res?.data?.data?.valid) {
        setNewPasswordStatus("valid");
        setFormError((prev) => ({ ...prev, new_password: "" }));
        return;
      }
      setNewPasswordStatus("invalid");
      setFormError((prev) => ({
        ...prev,
        new_password: res?.data?.message || strings.defaultPasswordNotAllowed,
      }));
    } catch (error) {
      if (requestId !== newPasswordRequest.current) {
        return;
      }
      setNewPasswordStatus("");
    }
  };

  const verifyCurrentPasswordValue = async (value) => {
    const password = String(value || "");
    if (!password.trim()) {
      setCurrentPasswordStatus("");
      return;
    }
    const requestId = currentPasswordRequest.current + 1;
    currentPasswordRequest.current = requestId;
    setCurrentPasswordStatus("checking");
    try {
      const res = await authService.verifyCurrentPassword({
        old_password: password,
        suppressGlobalLoader: true,
      });
      if (requestId !== currentPasswordRequest.current) {
        return;
      }
      if (res?.data?.status === 200 && res?.data?.data?.valid) {
        setCurrentPasswordStatus("valid");
        setFormError((prev) => ({ ...prev, old_password: "" }));
        return;
      }
      setCurrentPasswordStatus("invalid");
      setFormError((prev) => ({
        ...prev,
        old_password:
          res?.data?.message || strings.currentPasswordIncorrect,
      }));
    } catch (error) {
      if (requestId !== currentPasswordRequest.current) {
        return;
      }
      setCurrentPasswordStatus("");
    }
  };

  const handleUserInput = (name, value) => {
    setFormInput((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormError((prev) => ({
      ...prev,
      [name]: "",
    }));
    if (name === "new_password") {
      setNewPasswordStatus("");
      if (newPasswordTimer.current) {
        clearTimeout(newPasswordTimer.current);
      }
      newPasswordTimer.current = setTimeout(() => {
        verifyNewPasswordValue(value, formInput.old_password);
      }, 500);
    }
    if (name === "old_password") {
      setCurrentPasswordStatus("");
      if (currentPasswordTimer.current) {
        clearTimeout(currentPasswordTimer.current);
      }
      currentPasswordTimer.current = setTimeout(() => {
        verifyCurrentPasswordValue(value);
      }, 500);
      if (String(formInput.new_password || "").trim()) {
        if (newPasswordTimer.current) {
          clearTimeout(newPasswordTimer.current);
        }
        newPasswordTimer.current = setTimeout(() => {
          verifyNewPasswordValue(formInput.new_password, value);
        }, 500);
      }
    }
    if (
      name === "confirm_password" &&
      value &&
      String(formInput.new_password) !== String(value)
    ) {
      setFormError((prev) => ({
        ...prev,
        confirm_password: strings.passwordsDoNotMatch,
      }));
    }
  };

  const validateFormInput = () => {
    const inputError = { ...EMPTY_FORM };
    let valid = true;

    if (!String(formInput.new_password || "").trim()) {
      inputError.new_password = strings.newPasswordRequired;
      valid = false;
    } else if (newPasswordStatus === "invalid") {
      inputError.new_password = strings.defaultPasswordNotAllowed;
      valid = false;
    } else if (
      String(formInput.new_password) === String(formInput.old_password)
    ) {
      inputError.new_password = mustChangePassword
        ? strings.defaultPasswordNotAllowed
        : strings.newPasswordSameAsCurrent;
      valid = false;
    }
    if (!String(formInput.confirm_password || "").trim()) {
      inputError.confirm_password = strings.confirmPasswordRequired;
      valid = false;
    } else if (
      String(formInput.new_password) !== String(formInput.confirm_password)
    ) {
      inputError.confirm_password = strings.passwordsDoNotMatch;
      valid = false;
    }
    if (!String(formInput.old_password || "").trim()) {
      inputError.old_password = strings.currentPasswordRequired;
      valid = false;
    } else if (currentPasswordStatus === "invalid") {
      inputError.old_password = strings.currentPasswordIncorrect;
      valid = false;
    }

    setFormError(inputError);
    return valid;
  };

  const submitChangePassword = () => {
    if (!validateFormInput()) {
      return;
    }
    dispatch(
      changePasswordAction({
        old_password: formInput.old_password,
        new_password: formInput.new_password,
      })
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitChangePassword();
    }
  };

  const renderPasswordField = ({
    id,
    name,
    label,
    autoComplete,
    shown,
    onToggle,
  }) => (
    <div className="justo-change-password__field">
      <label className="justo-change-password__label" htmlFor={id}>
        {label}
      </label>
      <div className="justo-change-password__input-wrap">
        <input
          id={id}
          className="justo-change-password__input"
          type={shown ? "text" : "password"}
          name={name}
          autoComplete={autoComplete}
          value={formInput[name]}
          placeholder={label}
          onChange={({ target }) => handleUserInput(target.name, target.value)}
          onBlur={
            name === "new_password"
              ? ({ target }) =>
                  verifyNewPasswordValue(target.value, formInput.old_password)
              : name === "old_password"
              ? ({ target }) => verifyCurrentPasswordValue(target.value)
              : undefined
          }
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="justo-change-password__toggle"
          onClick={onToggle}
          aria-label={shown ? strings.hidePassword : strings.showPassword}
        >
          <i
            className={`fa ${shown ? "fa-eye" : "fa-eye-slash"}`}
            aria-hidden="true"
          />
        </button>
      </div>
      {name === "new_password" && newPasswordStatus === "checking" ? (
        <span className="justo-change-password__hint">
          {strings.newPasswordChecking}
        </span>
      ) : name === "new_password" && newPasswordStatus === "valid" ? (
        <span className="justo-change-password__ok">
          {strings.newPasswordCorrect}
        </span>
      ) : name === "old_password" && currentPasswordStatus === "checking" ? (
        <span className="justo-change-password__hint">
          {strings.currentPasswordChecking}
        </span>
      ) : name === "old_password" && currentPasswordStatus === "valid" ? (
        <span className="justo-change-password__ok">
          {strings.currentPasswordCorrect}
        </span>
      ) : formError[name] ? (
        <span className="justo-change-password__error">{formError[name]}</span>
      ) : null}
    </div>
  );

  return (
    <div
      className={`page-body justo-change-password-page${
        mustChangePassword ? " justo-change-password-page--force" : ""
      }`}
    >
      {mustChangePassword ? null : (
        <Breadcrumb
          title={strings.changePassword}
          titleactive={strings.changePassword}
        />
      )}
      <div className="container-fluid">
        <div className="justo-change-password__card">
          <div className="justo-change-password__top">
            <h1 className="justo-change-password__title">
              {mustChangePassword
                ? strings.forceChangePasswordTitle
                : strings.changePassword}
            </h1>
            <p className="justo-change-password__subtitle">
              {mustChangePassword
                ? strings.forceChangePasswordMessage
                : strings.enterNewPass}
            </p>
          </div>

          {mustChangePassword ? (
            <div className="justo-change-password__notice" role="status">
              <i className="fa fa-lock" aria-hidden="true" />
              <p>{strings.forceChangePasswordNotice}</p>
            </div>
          ) : null}

          <div className="justo-change-password__body">
            <section className="justo-change-password__panel">
              {renderPasswordField({
                id: "change_current_password",
                name: "old_password",
                label: strings.currentPassword,
                autoComplete: "current-password",
                shown: showCurrent,
                onToggle: () => setShowCurrent((v) => !v),
              })}
              {renderPasswordField({
                id: "change_new_password",
                name: "new_password",
                label: strings.newPass,
                autoComplete: "new-password",
                shown: showNew,
                onToggle: () => setShowNew((v) => !v),
              })}
              {renderPasswordField({
                id: "change_confirm_password",
                name: "confirm_password",
                label: strings.confirmPass,
                autoComplete: "new-password",
                shown: showConfirm,
                onToggle: () => setShowConfirm((v) => !v),
              })}
            </section>
          </div>

          <div className="justo-change-password__footer">
            {mustChangePassword ? (
              <button
                type="button"
                className="justo-change-password__btn justo-change-password__btn--ghost"
                onClick={() => dispatch(userLogout())}
              >
                {strings.signOut}
              </button>
            ) : (
              <button
                type="button"
                className="justo-change-password__btn justo-change-password__btn--ghost"
                onClick={() => navigate(-1)}
              >
                {strings.cancel}
              </button>
            )}
            <button
              type="button"
              className="justo-change-password__btn justo-change-password__btn--solid"
              onClick={submitChangePassword}
            >
              <i className="fa fa-check" aria-hidden="true" />
              {strings.changepassword}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
